import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  MagnifyingGlass, ShoppingCart, Heart, User,
  ArrowRight, ArrowLeft, Package, Truck, Shield,
  CheckCircle, ChatCircle, Phone, Envelope, MapPin, CaretDown, Star
} from '@phosphor-icons/react';
import axios from 'axios';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { sanitizeHTML } from '../../utils/sanitize';
import MagereStorefront from './MagereStorefront';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_STORE_SLUG = "mrg";

const StorefrontPage = ({ storeSlug: propStoreSlug }) => {
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || DEFAULT_STORE_SLUG;
  const standalone = !!propStoreSlug || !paramStoreSlug;
  const [store, setStore] = useState(null);
  const [template, setTemplate] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cmsPages, setCmsPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sort, setSort] = useState('newest');
  const [view, setView] = useState('home');
  const { addItem, items, removeItem, updateQuantity, total, itemCount, setIsOpen, setStoreSlug } = useCart();
  const { user } = useAuth();
  const userType = user?.client_type || 'particulier';

  // Wrap addItem to inject user type
  const addItemWithType = (product, qty = 1, variation = null, slug = null) => {
    addItem(product, qty, variation, slug, userType);
  };

  const id = useMemo(() => store?.identity || {}, [store]);
  const colors = useMemo(() => {
    const tc = template?.colors || {};
    return {
      primary: tc.primary || id.primary_color || '#1a3d5c',
      secondary: tc.secondary || id.secondary_color || '#0cb5c6',
      accent: tc.accent || id.accent_color || '#e0a800',
      footerBg: tc.footer_bg || '#c8dfe9',
    };
  }, [template, id]);

  const logoUrl = id.logo_light || store?.theme?.assets?.logo_url || '';

  useEffect(() => { if (storeSlug) setStoreSlug(storeSlug); }, [storeSlug, setStoreSlug]);

  useEffect(() => {
    (async () => {
      try {
        const [s, p, c, t, pg] = await Promise.all([
          axios.get(`${API}/api/s/${storeSlug}`),
          axios.get(`${API}/api/s/${storeSlug}/products?limit=500`),
          axios.get(`${API}/api/s/${storeSlug}/categories`),
          axios.get(`${API}/api/s/${storeSlug}/template`),
          axios.get(`${API}/api/s/${storeSlug}/pages`),
        ]);
        setStore(s.data);
        setProducts(p.data.products || []);
        setCategories(c.data || []);
        setTemplate(t.data);
        setCmsPages(pg.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [storeSlug]);

  useEffect(() => {
    if (!store || view !== 'shop') return;
    const fetchProducts = async () => {
      const p = new URLSearchParams();
      p.append('limit', '500');
      try { const r = await axios.get(`${API}/api/s/${storeSlug}/products?${p}`); setProducts(r.data.products || []); } catch (e) { console.error('Products fetch error:', e); }
    };
    const t = setTimeout(fetchProducts, 300);
    return () => clearTimeout(t);
  }, [store, storeSlug, view]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#1a3d5c' }} />
    </div>
  );
  if (!store) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <h1 className="text-3xl font-bold text-slate-800">Boutique introuvable</h1>
    </div>
  );

  /* ═══ Theme template routing ═══ */
  const themeTemplate = store?.identity?.theme_template || '';
  // Always use MagereStorefront for mrg store (standalone mode)
  if (themeTemplate === 'magere' || storeSlug === 'mrg' || storeSlug === 'magere-viandes' || standalone) {
    return <MagereStorefront store={store} template={template} products={products}
      categories={categories} cmsPages={cmsPages} search={search} setSearch={setSearch}
      selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
      sort={sort} setSort={setSort} view={view} setView={setView}
      addItem={addItemWithType} itemCount={itemCount} setIsOpen={setIsOpen} storeSlug={storeSlug}
      cartItems={items} removeItem={removeItem} updateQuantity={updateQuantity} cartTotal={total}
      standalone={standalone} userType={userType} />;
  }

  const goCategory = (cid) => { setSelectedCategory(cid); setView('shop'); };
  const blocks = template?.blocks || [];

  /* ═══════════ PRODUCT CARD ═══════════ */
  const ProductCard = ({ product }) => (
    <Link to={`/store/${storeSlug}/product/${product.slug}`} className="group block" data-testid={`product-card-${product.id}`}>
      <div className="bg-white overflow-hidden transition-all duration-300 hover:shadow-xl border border-slate-100 relative">
        <div className="aspect-square overflow-hidden bg-slate-50 relative">
          {(product.featured_image || product.images?.[0]) ? (
            <img src={product.featured_image || product.images[0]} alt={product.name}
              className="w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-110" loading="lazy" />
          ) : <div className="w-full h-full flex items-center justify-center"><Package size={48} className="text-slate-200" /></div>}
          {product.sale_price > 0 && product.price > 0 && (
            <span className="absolute top-2 left-2 px-2 py-0.5 text-[11px] font-bold text-white" style={{ backgroundColor: '#e74c3c' }}>
              -{Math.round((1 - product.sale_price / product.price) * 100)}%
            </span>
          )}
          {/* Quick actions on hover */}
          <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
            <button className="w-full py-2.5 text-[12px] font-bold text-white tracking-wide uppercase transition-colors"
              style={{ backgroundColor: colors.secondary }}
              onClick={(e) => { e.preventDefault(); addItem(product, 1, null, storeSlug); }}
              data-testid={`add-to-cart-${product.id}`}>
              Ajouter au panier
            </button>
          </div>
        </div>
        <div className="p-3 text-center">
          <h3 className="text-[12px] font-medium text-slate-700 line-clamp-2 leading-tight min-h-[32px] uppercase mb-2">{product.name}</h3>
          <div>
            {product.sale_price > 0 ? (
              <div className="flex items-center justify-center gap-2">
                <span className="line-through text-slate-400 text-[13px]">{product.price.toFixed(2)}&nbsp;&euro;</span>
                <span className="font-bold text-[15px]" style={{ color: '#e74c3c' }}>{product.sale_price.toFixed(2)}&nbsp;&euro;</span>
              </div>
            ) : product.price > 0 ? (
              <span className="font-bold text-[15px]" style={{ color: colors.primary }}>{product.price.toFixed(2)}&nbsp;&euro;</span>
            ) : <span className="text-xs text-slate-400">Prix sur demande</span>}
            <p className="text-[10px] text-slate-400 mt-0.5">TTC</p>
          </div>
        </div>
      </div>
    </Link>
  );

  /* ═══════════ HEADER (XStore 1:1 clone of toutpourstunt.fr) ═══════════ */
  const Header = () => (
    <header className="sticky top-0 z-50 bg-white" data-testid="store-header">
      {/* Main header — logo | search | phone | icons */}
      <div className="bg-white">
        <div className="max-w-[1300px] mx-auto px-6 flex items-center justify-between" style={{ height: 90 }}>
          {/* Logo */}
          <Link to={`/store/${storeSlug}`} className="flex-shrink-0" onClick={() => setView('home')} data-testid="store-logo">
            {logoUrl ? <img src={logoUrl} alt={store.name} className="h-[65px] max-w-[260px] object-contain" />
             : <span className="text-2xl font-extrabold tracking-tight" style={{ color: colors.primary }}>{store.name}</span>}
          </Link>

          {/* Search bar */}
          <div className="hidden md:flex items-center flex-1 max-w-[460px] mx-8">
            <div className="flex w-full overflow-hidden rounded-md" style={{ border: `2px solid ${colors.primary}` }}>
              <input placeholder="Je recherche un Pad bleu" value={search}
                onChange={e => { setSearch(e.target.value); if (e.target.value) setView('shop'); }}
                onKeyDown={e => { if (e.key === 'Enter' && search) setView('shop'); }}
                className="flex-1 h-[42px] px-4 text-[14px] outline-none bg-white placeholder-slate-400"
                style={{ fontFamily: 'inherit' }}
                data-testid="store-search" />
              <button className="w-[48px] h-[42px] flex items-center justify-center text-white transition-colors rounded-r-md"
                style={{ backgroundColor: colors.primary }}
                onClick={() => { if (search) setView('shop'); }}
                data-testid="search-btn">
                <MagnifyingGlass size={18} weight="bold" />
              </button>
            </div>
          </div>

          {/* Phone */}
          <div className="hidden lg:flex items-center gap-2.5 mr-6">
            <div className="w-[40px] h-[40px] rounded-full flex items-center justify-center" style={{ backgroundColor: '#4CAF50' }}>
              <Phone size={20} weight="fill" className="text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-[11px] text-slate-500 leading-tight">Une question ?</span>
              <span className="text-[15px] font-bold leading-tight" style={{ color: colors.primary }}>07 73 79 19 96</span>
            </div>
          </div>

          {/* Account / Favorites / Cart icons */}
          <div className="flex items-center gap-5">
            <Link to="/login" className="hidden md:flex flex-col items-center gap-0.5 text-slate-600 hover:text-slate-800 transition-colors"
              data-testid="account-btn">
              <User size={26} weight="thin" />
              <span className="text-[10px] leading-tight">Créer un compte</span>
            </Link>
            <button className="hidden md:flex flex-col items-center gap-0.5 relative transition-colors"
              style={{ color: colors.accent }}
              onClick={() => setView('shop')}
              data-testid="favorites-btn">
              <div className="relative">
                <Heart size={26} weight="thin" />
                <span className="absolute -top-1.5 -right-2 w-[16px] h-[16px] text-white text-[9px] font-bold rounded-full flex items-center justify-center"
                  style={{ backgroundColor: colors.secondary }}>0</span>
              </div>
              <span className="text-[10px] leading-tight" style={{ color: colors.accent }}>Mes favoris</span>
            </button>
            <button className="relative flex flex-col items-center gap-0.5 text-slate-600 hover:text-slate-800 transition-colors"
              onClick={() => setIsOpen(true)} data-testid="cart-btn">
              <div className="relative">
                <ShoppingCart size={26} weight="thin" />
                <span className="absolute -top-1.5 -right-2 w-[16px] h-[16px] text-white text-[9px] font-bold rounded-full flex items-center justify-center"
                  style={{ backgroundColor: colors.secondary }}>{itemCount}</span>
              </div>
              <span className="text-[10px] leading-tight">Panier</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation bar */}
      <nav style={{ backgroundColor: colors.primary }} data-testid="store-nav">
        <div className="max-w-[1300px] mx-auto px-6 flex items-center h-[44px] overflow-x-auto">
          {/* Category links with separators */}
          <div className="flex items-center gap-0">
            <button onClick={() => { setSelectedCategory(''); setSearch(''); setView('home'); }}
              className="px-4 h-[44px] text-[13px] font-medium whitespace-nowrap transition-all hover:bg-white/10 text-white"
              style={{ color: view === 'home' ? colors.accent : '#fff' }}
              data-testid="nav-home">
              Accueil
            </button>
            <span className="text-white/30 text-[14px] select-none">|</span>
            {categories.slice(0, 7).map((c, i) => (
              <React.Fragment key={c.id}>
                <button onClick={() => goCategory(c.id)}
                  className="px-4 h-[44px] text-[13px] font-medium whitespace-nowrap transition-all hover:bg-white/10 text-white"
                  style={{ color: selectedCategory === c.id ? colors.accent : '#fff' }}
                  data-testid={`nav-cat-${c.id}`}>
                  {c.name}
                </button>
                {i < Math.min(categories.length, 7) - 1 && (
                  <span className="text-white/30 text-[14px] select-none">|</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Special CTA button */}
          <button onClick={() => { setSelectedCategory(''); setView('shop'); }}
            className="hidden md:flex items-center gap-1.5 px-5 h-[30px] rounded-full text-[12px] font-bold whitespace-nowrap transition-all hover:opacity-90"
            style={{ border: `2px solid ${colors.accent}`, color: colors.accent, backgroundColor: 'transparent' }}
            data-testid="nav-all-products">
            Tous les produits
            <span className="ml-1 px-1.5 py-0.5 text-[9px] font-semibold rounded-full text-white"
              style={{ backgroundColor: colors.accent }}>
              NEW
            </span>
          </button>

          {/* Contact link */}
          <Link to={`/store/${storeSlug}/page/a-propos`}
            className="hidden md:flex items-center gap-1.5 ml-4 px-3 h-[44px] text-[13px] font-medium whitespace-nowrap transition-all hover:bg-white/10"
            style={{ color: colors.secondary }}
            data-testid="nav-contact">
            <ChatCircle size={16} weight="fill" />
            Contact
          </Link>
        </div>
      </nav>
    </header>
  );

  /* ═══════════ HERO SLIDESHOW (no text overlay — pure slideshow like .fr) ═══════════ */
  const HeroSlideshow = ({ config }) => {
    const slides = config?.slides || [];
    const [cur, setCur] = useState(0);
    useEffect(() => {
      if (slides.length <= 1) return;
      const iv = setInterval(() => setCur(p => (p + 1) % slides.length), config?.duration || 3000);
      return () => clearInterval(iv);
    }, [slides.length, config?.duration]);
    if (!slides.length) return null;
    return (
      <section className="relative w-full overflow-hidden" style={{ height: '75vh', minHeight: 480 }} data-testid="hero-slideshow">
        {slides.map((src, i) => (
          <div key={i} className="absolute inset-0 transition-opacity duration-700" style={{ opacity: i === cur ? 1 : 0, zIndex: i === cur ? 1 : 0 }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: `url(${src})`,
              animation: i === cur && config?.ken_burns ? 'kb 8s ease-in-out alternate infinite' : 'none',
            }} />
          </div>
        ))}
        <style>{`@keyframes kb{0%{transform:scale(1)}100%{transform:scale(1.12)}}`}</style>
      </section>
    );
  };

  /* ═══════════ TEXT COLUMNS (blue background overlay with bottom separator) ═══════════ */
  const TextColumns = ({ config }) => {
    const cols = config?.columns || [];
    if (!cols.length) return null;
    return (
      <section className="relative overflow-hidden" data-testid="text-columns">
        <div className="py-14" style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, #0d2c45 100%)` }}>
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23fff' fill-opacity='1'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`
          }} />
          <div className="max-w-[1200px] mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {cols.map((col, i) => (
                <div key={i} className="text-white/90 text-[13.5px] leading-[1.85] text-justify [&_h3]:text-[16px] [&_h3]:font-bold [&_h3]:mb-3 [&_h3]:text-white [&_strong]:text-white [&_u]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_p]:mb-3"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(col.html || '') }} />
              ))}
            </div>
          </div>
        </div>
        {/* XStore-style angled separator */}
        <svg className="w-full block -mt-px" viewBox="0 0 1440 50" preserveAspectRatio="none" style={{ height: 50 }}>
          <path d="M0,0 L1440,50 L0,50 Z" fill={colors.primary} />
        </svg>
      </section>
    );
  };

  /* ═══════════ CATEGORY CAROUSEL (circular images — XStore style) ═══════════ */
  const CategoryCarousel = ({ config }) => {
    const catRef = useRef(null);

    const mergedCats = useMemo(() => {
      const tCats = config?.categories || [];
      return tCats.map(tc => {
        const dbCat = categories.find(c => c.slug === tc.slug || c.name === tc.name);
        return { ...tc, id: dbCat?.id || '', productCount: dbCat ? undefined : tc.count_text };
      });
    }, [config?.categories, categories]);

    const scroll = (dir) => {
      if (catRef.current) catRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
    };

    return (
      <section className="py-14 bg-white" data-testid="category-carousel">
        <div className="max-w-[1300px] mx-auto px-4">
          <h2 className="text-center text-[24px] font-bold mb-10 tracking-wide" style={{ color: colors.primary }}>
            {config?.title || 'Nos rayons'}
          </h2>
          <div className="relative group/carousel">
            <button onClick={() => scroll(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-lg border flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity -ml-2 hover:bg-slate-50">
              <ArrowLeft size={16} />
            </button>
            <div ref={catRef} className="flex gap-8 overflow-x-auto px-2 py-2 scrollbar-hide scroll-smooth" style={{ scrollbarWidth: 'none' }}>
              {mergedCats.map((cat, i) => (
                <button key={i} onClick={() => cat.id ? goCategory(cat.id) : setView('shop')}
                  className="flex-shrink-0 flex flex-col items-center group/cat" style={{ width: 170 }}
                  data-testid={`cat-${cat.slug}`}>
                  <div className="w-[140px] h-[140px] rounded-full overflow-hidden mb-4 border-[3px] border-transparent transition-all duration-300 group-hover/cat:border-[3px] shadow-sm group-hover/cat:shadow-lg"
                    style={{ '--hover-border': colors.accent }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = colors.accent}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}>
                    {cat.image_url ? (
                      <img src={cat.image_url} alt={cat.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover/cat:scale-110" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-100"><Package size={36} className="text-slate-300" /></div>
                    )}
                  </div>
                  <h4 className="text-[12px] font-bold text-center uppercase tracking-wider mb-1" style={{ color: '#333' }}>{cat.name}</h4>
                  <span className="text-[11px] font-medium" style={{ color: '#999' }}>{cat.count_text || ''}</span>
                  <span className="text-[11px] font-semibold mt-1 group-hover/cat:underline" style={{ color: colors.secondary }}>Voir plus</span>
                </button>
              ))}
            </div>
            <button onClick={() => scroll(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-lg border flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity -mr-2 hover:bg-slate-50">
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>
    );
  };

  /* ═══════════ BRAND LOGOS (2 rows x 5) ═══════════ */
  const BrandLogos = ({ config }) => {
    const logos = config?.logos || [];
    if (!logos.length) return null;
    const half = Math.ceil(logos.length / 2);
    return (
      <section className="py-10 bg-white" data-testid="brand-logos">
        <div className="max-w-[1000px] mx-auto px-4 space-y-6">
          {[logos.slice(0, half), logos.slice(half)].map((row, ri) => (
            <div key={ri} className="flex items-center justify-center gap-10 md:gap-16 flex-wrap">
              {row.map((src, i) => (
                <div key={i} className="opacity-50 hover:opacity-100 transition-opacity duration-300 cursor-pointer">
                  <img src={src} alt="Marque partenaire" className="h-[26px] md:h-[32px] w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300" loading="lazy" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>
    );
  };

  /* ═══════════ BLOG CAROUSEL ═══════════ */
  const BlogCarousel = ({ config }) => {
    const posts = config?.posts || [];
    if (!posts.length) return null;
    return (
      <section className="py-14 bg-[#f4f7f9]" data-testid="blog-section">
        <div className="max-w-[1300px] mx-auto px-4">
          <h2 className="text-center text-[24px] font-bold mb-10 tracking-wide" style={{ color: colors.secondary }}>
            {config?.title || 'Nos conseils & Actu'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {posts.map((post, i) => (
              <article key={i} className="bg-white overflow-hidden transition-shadow hover:shadow-lg border border-slate-100">
                <div className="p-5">
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {(post.tags || []).map((tag, ti) => (
                      <span key={ti} className="text-[10px] px-2.5 py-0.5 rounded-full text-white font-semibold" style={{ backgroundColor: colors.secondary }}>{tag}</span>
                    ))}
                  </div>
                  <h3 className="text-[14px] font-bold mb-2 line-clamp-2 leading-snug" style={{ color: colors.primary }}>{post.title}</h3>
                  <p className="text-[12px] text-slate-500 line-clamp-5 leading-relaxed">{post.excerpt}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    );
  };

  /* ═══════════ PRODUCT GRID (only on shop page) ═══════════ */
  const ProductGrid = ({ config }) => {
    const limit = config?.limit || 12;
    const displayProducts = products.slice(0, limit);
    return (
      <section className="py-14 bg-[#fafbfc]" data-testid="product-grid">
        <div className="max-w-[1300px] mx-auto px-4">
          <h2 className="text-center text-[24px] font-bold mb-10 tracking-wide" style={{ color: colors.accent }}>
            {config?.title || 'Nos produits'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {displayProducts.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
          {products.length > limit && (
            <div className="text-center mt-10">
              <button onClick={() => { setSelectedCategory(''); setView('shop'); }}
                className="px-10 py-3 font-bold text-[13px] uppercase tracking-wider border-2 transition-all duration-300 hover:shadow-lg"
                style={{ borderColor: colors.primary, color: colors.primary }}
                onMouseEnter={e => { e.target.style.backgroundColor = colors.primary; e.target.style.color = '#fff'; }}
                onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = colors.primary; }}>
                Voir tous les produits
              </button>
            </div>
          )}
        </div>
      </section>
    );
  };

  /* ═══════════ TRUST BADGES (Notre force — 1:1 toutpourstunt.fr) ═══════════ */
  const TrustBadges = () => (
    <section className="py-14 bg-white" data-testid="trust-badges">
      <div className="max-w-[1100px] mx-auto px-6">
        <h2 className="text-center text-[24px] font-bold mb-10 tracking-wide" style={{ color: colors.primary }}>Notre force</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: <Star size={22} weight="fill" />, label: "+10 D'expériences", c: colors.accent },
            { icon: <Package size={22} weight="fill" />, label: "+260 références", c: colors.secondary },
            { icon: <CheckCircle size={22} weight="fill" />, label: "Pièces Compatibles", c: colors.primary },
            { icon: <Heart size={22} weight="fill" />, label: "Par des passionnés", c: '#e74c3c' },
            { icon: <MapPin size={22} weight="fill" />, label: "Stocks en France", c: colors.primary },
            { icon: <Truck size={22} weight="fill" />, label: "Livraison France & DOM-TOM", c: colors.secondary },
            { icon: <ChatCircle size={22} weight="fill" />, label: "Réponse rapide 15 min", c: colors.accent },
            { icon: <Shield size={22} weight="fill" />, label: "Points relais disponibles", c: colors.primary },
          ].map((b, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-4 rounded-xl bg-slate-50 border border-slate-100 transition-all hover:shadow-md hover:-translate-y-0.5">
              <span style={{ color: b.c }}>{b.icon}</span>
              <span className="text-[12px] font-bold" style={{ color: colors.primary }}>{b.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  /* ═══════════ FOOTER (dark navy — 1:1 toutpourstunt.fr) ═══════════ */
  const Footer = () => (
    <footer style={{ backgroundColor: colors.primary }} className="relative" data-testid="store-footer">
      <div className="max-w-[1300px] mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
          {/* Col 1: Logo + desc */}
          <div className="col-span-2 md:col-span-1">
            {logoUrl ? <img src={logoUrl} alt={store.name} className="h-[70px] mb-4 object-contain brightness-0 invert" />
             : <span className="text-lg font-extrabold block mb-4 text-white">{store.name}</span>}
            <p className="text-[12px] leading-relaxed text-white/60 text-justify">
              Site de référence pour toutes les pièces détachées pour <strong className="text-white">STUNT, SLIDER, NITRO, OVETTO, BOOSTER</strong>, des marques <strong className="text-white">MBK & YAMAHA</strong>, pour reconstituer vos scooters dans la configuration d'origine !
            </p>
          </div>
          {/* Col 2: Categories */}
          <div>
            <h4 className="text-[13px] font-bold mb-4 text-white">Categories</h4>
            <ul className="space-y-2">
              {categories.slice(0, 5).map(c => (
                <li key={c.id}>
                  <Link to={`/store/${storeSlug}/category/${c.slug || c.id}`}
                    onClick={(e) => { e.preventDefault(); goCategory(c.id); }}
                    className="text-[12px] text-white/60 hover:text-white transition-colors cursor-pointer">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          {/* Col 3: Mon compte */}
          <div>
            <h4 className="text-[13px] font-bold mb-4" style={{ color: colors.secondary }}>Mon compte</h4>
            <ul className="space-y-2 text-[12px] text-white/60">
              <li><span className="hover:text-white transition-colors cursor-pointer">Mon compte</span></li>
              <li><span className="hover:text-white transition-colors cursor-pointer">Mes commandes</span></li>
              {cmsPages.filter(p => p.is_published).map(p => (
                <li key={p.id}>
                  <Link to={`/store/${storeSlug}/page/${p.slug}`} className="hover:text-white transition-colors">{p.title}</Link>
                </li>
              ))}
            </ul>
            <button onClick={() => { setSelectedCategory(''); setView('shop'); }}
              className="mt-4 px-4 py-2.5 rounded-md text-[11px] font-bold text-white transition-all hover:opacity-90"
              style={{ backgroundColor: colors.secondary }}
              data-testid="footer-cta">
              Demande KIT DECO personnalisé
            </button>
          </div>
          {/* Col 4: Liens rapides */}
          <div>
            <h4 className="text-[13px] font-bold mb-4 text-white">Liens rapides</h4>
            <ul className="space-y-2 text-[12px]">
              <li><button onClick={() => setView('home')} className="hover:text-white transition-colors" style={{ color: colors.secondary }}>Nos conseils & Actu</button></li>
              <li><button onClick={() => setView('home')} className="hover:text-white transition-colors" style={{ color: colors.secondary }}>FAQ's</button></li>
              {cmsPages.filter(p => p.is_published).slice(0, 3).map(p => (
                <li key={p.id}>
                  <Link to={`/store/${storeSlug}/page/${p.slug}`} className="hover:text-white transition-colors" style={{ color: colors.secondary }}>{p.title}</Link>
                </li>
              ))}
            </ul>
          </div>
          {/* Col 5: Service clients */}
          <div>
            <h4 className="text-[13px] font-bold mb-2" style={{ color: colors.secondary }}>Service clients</h4>
            <p className="text-[22px] font-extrabold text-white mb-1">07 73 79 19 96</p>
            <p className="text-[12px] text-white/60 mb-4">contact@toutpourstunt.fr</p>
            <h4 className="text-[13px] font-bold mb-2" style={{ color: colors.secondary }}>Suivez nous sur</h4>
            <div className="flex gap-2">
              {['instagram', 'snapchat', 'tiktok'].map(s => (
                <div key={s} className="w-[32px] h-[32px] rounded-full flex items-center justify-center text-white text-[14px] font-bold"
                  style={{ backgroundColor: colors.accent }}>
                  {s[0].toUpperCase()}
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Bottom bar */}
        <div className="border-t border-white/10 mt-10 pt-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <span className="text-[11px] text-white/50">
            Copyright &copy; {new Date().getFullYear()} — Tous droits réservés à {store.name} — Reproduction ou copie interdite.
          </span>
          <div className="flex items-center gap-3">
            <span className="text-[11px]" style={{ color: colors.secondary }}>Paiement Par CB Sécurisé</span>
            <div className="flex gap-1.5">
              {['VISA', 'UPI', 'Skrill', 'Apple Pay'].map(p => (
                <div key={p} className="px-2.5 py-1 bg-white rounded text-[9px] font-bold text-slate-700 border border-white/20">{p}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );

  /* ═══════════ SHOP VIEW ═══════════ */
  const ShopView = () => (
    <div className="max-w-[1300px] mx-auto px-4 py-8" data-testid="shop-view">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold" style={{ color: colors.primary }}>
            {selectedCategory ? categories.find(c => c.id === selectedCategory)?.name || 'Catégorie' : 'Tous les produits'}
          </h1>
          <p className="text-[12px] text-slate-400">{products.length} produit(s)</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px] text-[12px] h-9 border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Plus récents</SelectItem>
              <SelectItem value="price_asc">Prix croissant</SelectItem>
              <SelectItem value="price_desc">Prix décroissant</SelectItem>
              <SelectItem value="name">Nom A-Z</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex gap-6">
        <aside className="hidden lg:block w-[220px] flex-shrink-0">
          <div className="sticky top-[160px]">
            <h3 className="font-bold text-[13px] mb-3 uppercase tracking-wider" style={{ color: colors.primary }}>Catégories</h3>
            <ul className="space-y-0.5">
              <li>
                <button onClick={() => setSelectedCategory('')}
                  className={`text-[13px] py-1.5 block w-full text-left transition-colors ${!selectedCategory ? 'font-bold' : 'hover:translate-x-1'}`}
                  style={{ color: !selectedCategory ? colors.accent : '#555', transition: 'all 0.2s' }}>
                  Tout voir
                </button>
              </li>
              {categories.map(c => (
                <li key={c.id}>
                  <button onClick={() => setSelectedCategory(c.id)}
                    className={`text-[13px] py-1.5 block w-full text-left transition-all ${selectedCategory === c.id ? 'font-bold' : 'hover:translate-x-1'}`}
                    style={{ color: selectedCategory === c.id ? colors.accent : '#555' }}>
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>
        <div className="flex-1">
          {products.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          ) : (
            <div className="text-center py-20 text-slate-400">
              <Package size={56} className="mx-auto mb-4" weight="thin" />
              <p className="text-lg">Aucun produit trouvé</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  /* ═══════════ BLOCK RENDERER ═══════════ */
  const BlockRenderer = ({ block }) => {
    switch (block.type) {
      case 'hero-slideshow': return <HeroSlideshow config={block.config} />;
      case 'text-columns': return <TextColumns config={block.config} />;
      case 'category-carousel': return <CategoryCarousel config={block.config} />;
      case 'product-grid': return null; // No product grid on homepage (faithful to .fr)
      case 'brand-logos': return <BrandLogos config={block.config} />;
      case 'blog-carousel': return <BlogCarousel config={block.config} />;
      default: return null;
    }
  };

  /* ═══════════ MAIN RENDER ═══════════ */
  return (
    <div className="min-h-screen bg-white" data-testid="storefront-page" style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      <Header />
      {view === 'home' ? (
        <>
          {blocks.length > 0
            ? blocks.map((block, i) => <BlockRenderer key={`${block.type}-${i}`} block={block} />)
            : <HeroSlideshow config={{ slides: [], height: '60vh' }} />
          }
          <TrustBadges />
          {/* Angled separator before footer */}
          <svg className="w-full block -mb-px" viewBox="0 0 1440 50" preserveAspectRatio="none" style={{ height: 50 }}>
            <path d="M0,0 L1440,0 L1440,50 Z" fill={colors.primary} />
          </svg>
        </>
      ) : (
        <ShopView />
      )}
      <Footer />
    </div>
  );
};

export default StorefrontPage;
